import { useState, useEffect } from "react";
import DashboardHeader from "@/components/layout/DashboardHeader";
import IssueTable from "@/components/dashboard/IssueTable";
import GoogleMap from "@/components/dashboard/GoogleMap";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PlacesSearchMap from "@/components/dashboard/GoogleApi";

import HeatMap from "@/components/dashboard/HeatMap";




export enum CategoryType {
  GARBAGE = "GARBAGE",
  POTHOLE = "POTHOLE",
  ELECTRIC_HAZARD = "ELECTRIC_HAZARD",
  STRAY_CATTLE = "STRAY_CATTLE",
  CONSTRUCTION_DEBRIS = "CONSTRUCTION_DEBRIS",
  STAGNANT_WATER = "STAGNANT_WATER",
  BURNING_WASTE = "BURNING_WASTE",
}

export const CATEGORY_COLORS: Record<CategoryType, string> = {
  [CategoryType.GARBAGE]: "#22c55e",        // Green
  [CategoryType.POTHOLE]: "#ef4444",        // Red
  [CategoryType.ELECTRIC_HAZARD]: "#f59e0b", // Amber
  [CategoryType.STRAY_CATTLE]: "#fbbf24",   // Yellow
  [CategoryType.CONSTRUCTION_DEBRIS]: "#ea580c", // Orange
  [CategoryType.STAGNANT_WATER]: "#0ea5e9", // Blue
  [CategoryType.BURNING_WASTE]: "#dc2626",  // Dark Red
};



// Mock issue data
const mockIssues = [
  {
    id: "CR-83451",
    category: "electric-hazard",
    location: { lat: 40.7128, lng: -74.006 },
    dateReported: "2024-10-26 09:15 AM",
    status: "open" as const,
  },
  {
    id: "CR-83452",
    category: "stray-cattle",
    location: { lat: 34.0522, lng: -118.2437 },
    dateReported: "2024-10-26 08:45 AM",
    status: "open" as const,
  },
  {
    id: "CR-83449",
    category: "construction-debris",
    location: { lat: 41.8781, lng: -87.6298 },
    dateReported: "2024-10-25 11:20 PM",
    status: "closed" as const,
  },
  {
    id: "CR-83448",
    category: "stagnant-water",
    location: { lat: 40.7145, lng: -74.0081 },
    dateReported: "2024-10-25 04:30 PM",
    status: "in-progress" as const,
  },
  {
    id: "CR-83447",
    category: "burning-waste",
    location: { lat: 37.7749, lng: -122.4194 },
    dateReported: "2024-10-25 01:45 PM",
    status: "open" as const,
  },
  {
    id: "CR-83446",
    category: "electric-hazard",
    location: { lat: 39.9526, lng: -75.1652 },
    dateReported: "2024-10-25 10:30 AM",
    status: "in-progress" as const,
  },
  {
    id: "CR-83445",
    category: "stray-cattle",
    location: { lat: 29.7604, lng: -95.3698 },
    dateReported: "2024-10-24 07:20 PM",
    status: "closed" as const,
  },
  {
    id: "CR-83444",
    category: "construction-debris",
    location: { lat: 33.4484, lng: -112.074 },
    dateReported: "2024-10-24 03:15 PM",
    status: "closed" as const,
  },
];

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  // Load stats from localStorage or default to null
  const [stats, setStats] = useState(() => {
    const savedStats = localStorage.getItem("dashboardStats");
    if (savedStats) {
      try {
        const parsed = JSON.parse(savedStats);
        // Check if data is less than 30 minutes old (1800000 ms)
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 1800000) {
          return parsed.data;
        }
      } catch (error) {
        console.error("Error parsing saved stats:", error);
      }
    }
    return null;
  });
  // Load map locations from localStorage or default to empty array
  const [mapLocations, setMapLocations] = useState(() => {
    const savedLocations = localStorage.getItem("dashboardMapLocations");
    if (savedLocations) {
      try {
        const parsed = JSON.parse(savedLocations);
        // Check if data is less than 1 hour old (3600000 ms)
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 3600000) {
          return parsed.data;
        }
      } catch (error) {
        console.error("Error parsing saved map locations:", error);
      }
    }
    return [];
  });
  
  // Load active tab from localStorage or default to "issues"
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem("dashboardActiveTab");
    return savedTab || "issues";
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);

        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Authentication required");

        const response = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/reports/stats`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: any = await response.json();
        setStats(data.data);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchMapLocations = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const response = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/reports?limit=2000&severityScore=70`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const locations = data.data.reports.map((report: any) => ({
            lat: report.lat,
            lng: report.lng,
            id: report.id,
            category: report.category,
            status: report.status,
            icon: CATEGORY_COLORS[report.category as CategoryType],
            imageUrl: report.imageUrl,
            severity: report.severityScore,
          }));
          setMapLocations(locations);
        }
      } catch (err) {
        console.error("Failed to fetch map locations:", err);
      }
    };

    // Only fetch data if we don't have fresh cached data
    if (!stats) {
      fetchStats();
    } else {
      setIsLoading(false);
    }
    
    if (mapLocations.length === 0) {
      fetchMapLocations();
    }
  }, []);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("dashboardActiveTab", activeTab);
  }, [activeTab]);

  // Save map locations to localStorage whenever they change
  useEffect(() => {
    if (mapLocations.length > 0) {
      const dataWithTimestamp = {
        data: mapLocations,
        timestamp: Date.now()
      };
      localStorage.setItem("dashboardMapLocations", JSON.stringify(dataWithTimestamp));
    }
  }, [mapLocations]);

  // Save stats to localStorage whenever they change
  useEffect(() => {
    if (stats) {
      const dataWithTimestamp = {
        data: stats,
        timestamp: Date.now()
      };
      localStorage.setItem("dashboardStats", JSON.stringify(dataWithTimestamp));
    }
  }, [stats]);

  // Function to clear saved dashboard state
  const clearDashboardState = () => {
    localStorage.removeItem("dashboardActiveTab");
    localStorage.removeItem("dashboardMapLocations");
    localStorage.removeItem("dashboardStats");
  };

  // Function to refresh all data
  const refreshDashboardData = async () => {
    // Clear cached data
    clearDashboardState();
    
    // Reset states
    setStats(null);
    setMapLocations([]);
    setIsLoading(true);
    
    // Fetch fresh data
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        // Fetch stats
        const statsResponse = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/reports/stats`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData.data);
        }
        
        // Fetch map locations
        const locationsResponse = await fetch(
          `${import.meta.env.VITE_BE_URL}/api/v1/reports?limit=2000&severityScore=70`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (locationsResponse.ok) {
          const locationsData = await locationsResponse.json();
          const locations = locationsData.data.reports.map((report: any) => ({
            lat: report.lat,
            lng: report.lng,
            id: report.id,
            category: report.category,
            status: report.status,
            icon: CATEGORY_COLORS[report.category as CategoryType],
            imageUrl: report.imageUrl,
            severity: report.severityScore,
          }));
          setMapLocations(locations);
        }
      } catch (error) {
        console.error("Error refreshing dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Fetch fresh map data every time we switch to map tab
  useEffect(() => {
    if (activeTab === "map") {
      // Define fetchMapLocations function inline for this specific use case
      const fetchMapLocationsForTab = async () => {
        try {
          const token = localStorage.getItem("authToken");
          if (!token) return;

          const response = await fetch(
            `${import.meta.env.VITE_BE_URL}/api/v1/reports?limit=2000&severityScore=70`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            const locations = data.data.reports.map((report: any) => ({
              lat: report.lat,
              lng: report.lng,
              id: report.id,
              category: report.category,
              status: report.status,
              icon: CATEGORY_COLORS[report.category as CategoryType],
              imageUrl: report.imageUrl,
              severity: report.severityScore,
            }));
            setMapLocations(locations);
          }
        } catch (err) {
          console.error("Failed to fetch map locations:", err);
        }
      };

      fetchMapLocationsForTab();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DashboardHeader onRefresh={refreshDashboardData} />

      <main className="flex-grow container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Reported Issues Dashboard</h1>
          <p className="text-muted-foreground">
            View and manage all reported civic issues in one place
          </p>
        </div>

        {/* Dashboard stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">New Issues</div>
            <div className="text-2xl font-bold">
              {stats?.byStatus?.NEW ?? 0}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">In Review</div>
            <div className="text-2xl font-bold">
              {stats?.byStatus?.IN_REVIEW ?? 0}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Resolved Issues</div>
            <div className="text-2xl font-bold">
              {stats?.byStatus?.RESOLVED ?? 0}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Total Issues</div>
            <div className="text-2xl font-bold">{stats?.total ?? 0}</div>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="issues">Reported Issues</TabsTrigger>
            <TabsTrigger value="map">Map View</TabsTrigger>
            <TabsTrigger value="heatmap">Heatmap</TabsTrigger>
          </TabsList>

          <TabsContent value="issues" className="mt-6">
            <IssueTable />
          </TabsContent>

          <TabsContent value="map" className="mt-6">
            <GoogleMap key={`map-${activeTab}`} locations={mapLocations} />
          </TabsContent>
          <TabsContent value="heatmap" className="mt-6">
            <HeatMap 
              locations={mapLocations} 
              newIssuesCount={stats?.byStatus?.NEW ?? 0}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
